<template>
    <hts-router-drawer
        ref="routerDrawer"
        :title="$t('labels.editDocument')"
        :submit="submitEditDocument"
        :rules="rulesEditDocument"
        no-reset
        @loaded="getData"
        @refresh-tables="refreshTables">
        <hts-card :title="$t('labels.generalInformation')">
            <div class="grid gap-4">
                <hts-form-select
                    v-model="dataFormEditDocument.type"
                    :label="$t('labels.type')"
                    :items="typeDocument"
                    key-id="value"
                    key-label="title"
                    required
                    prop="type"
                    name="type" />
                <hts-form-select
                    v-if="typeCooperator.length >= 2 && dataFormEditDocument.type === 'CONTRACT'"
                    v-model="dataFormEditDocument.contractFor"
                    :label="$t('labels.contractFor')"
                    :items="selectTypeCooperator"
                    key-id="value"
                    key-label="title"
                    required
                    prop="contractFor"
                    name="contractFor" />
                <hts-form-file
                    v-if="!dataFormEditDocument.createContract || dataFormEditDocument.type !== 'CONTRACT'"
                    ref="file"
                    v-model="fileDocument"
                    type="file"
                    :label="$t('labels.selectFile')"
                    :color="props.color"
                    name="document"
                    prop="document"
                    :file-name="dataFormEditDocument.fileOriginName" />
                <hts-form-input
                    v-model="dataFormEditDocument.fileName"
                    :label="$t('labels.fileName')"
                    :color="props.color"
                    name="fileName"
                    prop="fileName" />
                <hts-form-input
                    v-model="dataFormEditDocument.description"
                    :label="$t('labels.description')"
                    :color="props.color"
                    name="description"
                    prop="description" />
                <hts-form-date
                    v-if="dataFormEditDocument.type === 'CONTRACT'"
                    v-model="dataFormEditDocument.effectiveDate"
                    :label="$t('labels.effectiveDate')"
                    type="date"
                    :color="props.color"
                    name="effectiveDate"
                    prop="effectiveDate" />
                <hts-form-date
                    v-if="dataFormEditDocument.type === 'CONTRACT'"
                    v-model="dataFormEditDocument.dueDate"
                    :label="$t('labels.dueDate')"
                    type="date"
                    :color="props.color"
                    name="dueDate"
                    prop="dueDate" />
                <hts-form-toggle
                    v-if="
                        dataFormEditDocument.type === 'CONTRACT' &&
                            dataFormEditDocument.createContract &&
                            !dataFormEditDocument.emailedApprove &&
                            dataFormEditDocument.contractStatus === 0
                    "
                    v-model="dataFormEditDocument.isSendEmailApprover"
                    :label="$t('labels.isSendEmailApprover')"
                    color="primary"
                    label-right />
            </div>
        </hts-card>
        <hts-card
            v-if="dataFormEditDocument.contractFor === 'CUSTOMER' && dataFormEditDocument.type === 'CONTRACT'"
            :title="$t('labels.creditTerms')">
            <div class="grid gap-4">
                <hts-form-radio
                    v-model="dataFormEditDocument.creditTermsBy"
                    :label="$t('labels.creditTermsBy')"
                    required
                    :items="[
                        { id: 'day', label: $t('labels.day') },
                        { id: 'month', label: $t('labels.month') }
                    ]"
                    name="creditTermsBy"
                    prop="creditTermsBy" />
                <hts-form-input
                    v-if="dataFormEditDocument.creditTermsBy === 'month'"
                    v-model="dataFormEditDocument.dayOfTheMonth"
                    :label="$t('labels.dayOfTheMonth')"
                    type="number"
                    :color="props.color"
                    name="dayOfTheMonth"
                    prop="dayOfTheMonth"
                    required />
                <hts-form-input
                    v-if="dataFormEditDocument.creditTermsBy === 'month'"
                    v-model="dataFormEditDocument.monthsCreditTerms"
                    :label="$t('labels.monthsCreditTerms')"
                    type="number"
                    :color="props.color"
                    name="monthsCreditTerms"
                    prop="monthsCreditTerms"
                    required />
                <hts-form-input
                    v-if="dataFormEditDocument.creditTermsBy === 'day'"
                    v-model="dataFormEditDocument.duration"
                    :label="$t('labels.duration')"
                    type="number"
                    :color="props.color"
                    name="duration"
                    prop="duration"
                    required />
                <hts-form-input
                    v-model="dataFormEditDocument.spendingLimit"
                    :label="$t('labels.spendingLimit')"
                    type="number"
                    :color="props.color"
                    required
                    name="spendingLimit"
                    prop="spendingLimit" />
            </div>
        </hts-card>

        <hts-modal
            :show="openPopupConfirmDueDate"
            :title="$t('labels.confirmDueDate')"
            type="confirm"
            :content="$t('labels.confirmDueDateDetail')"
            @hide="openPopupConfirmDueDate = false"
            @confirm="handleSubmitFormEditDocument" />

        <hts-card
            v-if="showChangeStatus && dataFormEditDocument.type === 'CONTRACT'"
            :title="$t('labels.contractStatus')">
            <div class="grid gap-4">
                <hts-form-select
                    v-model="dataFormEditDocument.stopEffect"
                    :label="$t('labels.status')"
                    :items="contractStatus"
                    key-id="value"
                    key-label="title"
                    required
                    prop="status"
                    name="status" />
            </div>
        </hts-card>
    </hts-router-drawer>
</template>

<script lang="ts" setup>
import layoutPermissions from '@/data/permissions/layout-permissions'
import { checkPermission } from '@/utils/permission-check'
import { ICooperatorDocument } from '~/pages/trucker-[id]/partner/-components/imports/form'

const props = defineProps({
  color: {
    type: String,
    default: 'theme'
  }
})

const _initForm = {
  dayOfTheMonth: null,
  monthsCreditTerms: null,
  duration: null,
  spendingLimit: null,
  cooperatorDocument: undefined,
  fileName: '',
  description: '',
  type: '',
  dueDate: '',
  creditTermsBy: '',
  effectiveDate: '',
  stopEffect: null,
  contractStatus: null,
  createContract: false,
  isSendEmailApprover: false,
  emailedApprove: false,
  contractFor: ''
}

const visible = ref(false)
const typeCooperator: { [key: string]: any } = ref([])
const routerDrawer = ref()
const openPopupConfirmDueDate = ref(false)
const showChangeStatus = ref(false)
const fileDocument = ref<File | string>('')

const dataFormEditDocument = ref<ICooperatorDocument>({ ..._initForm })
const emits = defineEmits(['close', 'refreshTables'])

const refreshTables = () => {
  emits('refreshTables')
}

const documentId = computed(() => useRoute().params.iddocumentpartner)
const cooperatorId = computed(() => useRoute().params.idpartner || '')

const selectTypeCooperator = computed(() => {
  return typeCooperator.value.map((type: string) => {
    return {
      value: type,
      title: $t(`labels.${type.toLowerCase()}`)
    }
  })
})

const submitEditDocument = async () => {
  if (dataFormEditDocument.value.contractFor === 'CUSTOMER' && dataFormEditDocument.value.type === 'CONTRACT') {
    const dayOfTheMonth = dataFormEditDocument.value.dayOfTheMonth
    const monthsCreditTerms = dataFormEditDocument.value.monthsCreditTerms
    const duration = dataFormEditDocument.value.duration
    if (!(dayOfTheMonth && monthsCreditTerms) && !duration) {
      process.client && $toast().error($t('notifications.documentValidateDateOfTheMonthAndDuration'))
      return
    }

    if (showChangeStatus.value) {
      const effectiveDate = dataFormEditDocument.value.effectiveDate + ' 00:00:00'
      const dueDate = dataFormEditDocument.value.dueDate + ' 23:59:59'
      const now = new Date()
      if ((effectiveDate && new Date(effectiveDate) > now) || (dueDate && new Date(dueDate) < now)) {
        process.client && $toast().error($t('labels.checkContractDueDate'))
        return
      }
    }
  }

  if (dataFormEditDocument.value.type === 'CONTRACT') {
    if (!dataFormEditDocument.value.dueDate) {
      openPopupConfirmDueDate.value = true
      return
    }
  }
  await handleSubmitFormEditDocument()
}

const handleSubmitFormEditDocument = async () => {
  const dataForm = new FormData()
  dataForm.append('fileName', dataFormEditDocument.value.fileName)
  dataForm.append('cooperator', cooperatorId.value.toString())
  dataForm.append('description', dataFormEditDocument.value.description)
  dataForm.append('type', dataFormEditDocument.value.type)

  const typeDocument = dataFormEditDocument.value.type
  if (typeDocument === 'CONTRACT') {
    dataForm.append('dueDate', dataFormEditDocument.value.dueDate || '')
    dataForm.append('effectiveDate', dataFormEditDocument.value.effectiveDate || '')
    dataForm.append('contractFor', dataFormEditDocument.value.contractFor || '')
  }

  if (typeDocument === 'CONTRACT' && typeCooperator.value && dataFormEditDocument.value.contractFor === 'CUSTOMER') {
    dataForm.append(
      'credit',
      JSON.stringify({
        creditTermsBy: dataFormEditDocument.value.creditTermsBy,
        dayOfTheMonth: dataFormEditDocument.value.dayOfTheMonth,
        duration: dataFormEditDocument.value.duration,
        spendingLimit: dataFormEditDocument.value.spendingLimit,
        monthsCreditTerms: dataFormEditDocument.value.monthsCreditTerms
      })
    )
  }

  if (dataFormEditDocument.value.createContract) {
    dataForm.append('isSendEmailApprover', String(dataFormEditDocument.value.isSendEmailApprover))
  }

  if (!dataFormEditDocument.value.createContract || typeDocument !== 'CONTRACT') {
    dataForm.append('cooperatorDocument', fileDocument.value)
  }

  if (dataFormEditDocument.value.createContract && typeDocument === 'CONTRACT') {
    dataForm.append('createContract', 'true')
  } else {
    dataForm.append('createContract', 'false')
  }

  const api = {
    url: '/partner/document/file/ID',
    method: 'put'
  }

  dataForm.append('stopEffect', String(dataFormEditDocument?.value?.stopEffect) || 'false')
  // if (dataFormEditDocument.value.stopEffect) {
  //   dataFormEditDocument.value.stopEffect = dataFormEditDocument.value.stopEffect === 'true'
  // }

  const data: any = await $api(api, { uriParams: { ID: documentId.value }, body: dataForm })
  if (data?.data?.value?.success) {
    process.client && $toast().success($t('notifications.editSuccessDocument'))
    visible.value = false
    if (routerDrawer.value) {
      routerDrawer.value.handleSubmitClose()
    }
  }
}
const openRouterView = ref()
const closeOverSide = () => {
  if (openRouterView.value) {
    openRouterView.value.close()
  }
}

const typeDocument = ref([
  {
    value: 'CONTRACT',
    title: $t('labels.contract')
  },
  {
    value: 'CERTIFICATEBUSINESS',
    title: $t('labels.certificateBusiness')
  },
  {
    value: 'OTHER',
    title: $t('labels.other')
  }
])

const contractStatus = ref([
  {
    value: 'false',
    title: $t('labels.effect')
  },
  {
    value: 'true',
    title: $t('labels.stopEffect')
  }
])

const rulesEditDocument = computed(() => {
  const rules = {
    type: 'required'
  } as { [key: string]: string }

  const typeDocument = dataFormEditDocument.value.type

  if (!dataFormEditDocument.value.createContract || (typeDocument && typeDocument !== 'CONTRACT')) {
    rules.document = 'fileSize:10'
  }

  if (typeDocument && typeDocument === 'CONTRACT') {
    if (dataFormEditDocument.value.effectiveDate) {
      rules.dueDate = `date_bigger:${dataFormEditDocument.value.effectiveDate}`
    }
  }

  if (typeDocument === 'CONTRACT' && dataFormEditDocument.value.contractFor === 'CUSTOMER') {
    rules.creditTermsBy = 'required'
    if (dataFormEditDocument.value.creditTermsBy === 'day') {
      rules.duration = 'required|integer'
    }
    if (dataFormEditDocument.value.creditTermsBy === 'month') {
      rules.dayOfTheMonth = 'required|min_value:1|max_value:28|integer'
      rules.monthsCreditTerms = 'required|integer'
    }
    rules.spendingLimit = 'required'
  }

  return rules
})

const loading = ref(false)
const getData = () => {
  loading.value = true
  const api = {
    url: '/partner/document/cooperatorId/detail/ID',
    method: 'get'
  }
  fileDocument.value = ''
  $api(api, { uriParams: { cooperatorId: cooperatorId.value, ID: documentId.value } })
    .then((data: any) => {
      const resData: any = data.data.value
      if (resData.success) {
        loading.value = false
        dataFormEditDocument.value = { ...resData.data }
        checkShowChangeStatus({ ...resData.data })
        dataFormEditDocument.value.stopEffect = String(resData.data.stopEffect)
        if (resData.data.cooperatorDetail.type) {
          typeCooperator.value = resData.data.cooperatorDetail.type
        }
        if (resData.data.cooperatorDetail.type.length === 1) {
          dataFormEditDocument.value.contractFor = resData.data.cooperatorDetail.type[0]
        }
      }
    })
    .catch(() => {
      loading.value = false
    })
}

const checkShowChangeStatus = (data: any) => {
  const effectiveDate = data.effectiveDate + ' 00:00:00'
  const dueDate = data.dueDate + ' 23:59:59'

  if (!checkPermission(layoutPermissions.partner_approve_contract)) {
    showChangeStatus.value = false
    return
  }

  if (data.type !== 'CONTRACT') {
    showChangeStatus.value = false
    return
  }

  if (data.contractStatus !== 1) {
    showChangeStatus.value = false
    return
  }

  if (data.stopEffect) {
    showChangeStatus.value = true
    return
  }

  const now = new Date()
  if (effectiveDate && new Date(effectiveDate) > now) {
    showChangeStatus.value = false
    return
  }

  if (dueDate && new Date(dueDate) < now) {
    showChangeStatus.value = false
    return
  }

  showChangeStatus.value = true
}
</script>
